import { dotClassOf, statusLabelOf } from '../lib/appearance';
import { endOf, startOf, titleOf } from '../lib/adapters';
import { messages } from '@/shared/constants/messages';
import { formatDayParts, formatTime, isSameZonedDay } from '@/shared/lib/dates';
import { formatList } from '@/shared/lib/format';
import { cn } from '@/shared/lib/utils';
import type { CalendarItem } from '@/shared/types/domain';

interface DayAgendaListProps {
  /** `yyyy-mm-dd` — sempre há um dia selecionado (decisão 72). */
  day: string;
  items: readonly CalendarItem[];
  onOpenItem: (item: CalendarItem) => void;
}

/**
 * O que acontece no dia selecionado — **e por que isto NÃO é o `ProjectTable`**
 * (spec-calendar-ux.md §5.2, emendando a decisão 71).
 *
 * A regra da casa é que `ProjectTable` é a única forma de listar projeto no
 * painel, e ela continua valendo onde o painel LISTA PROJETO: `/projetos`,
 * `/analise`, coordenações. Duas coisas tiram esta lista de lá:
 *
 * 1. **Ela não cabe.** `ProjectTable` esconde coluna por *viewport*, não por
 *    container (`hidden lg:table-cell`) — numa coluna de 320px de uma tela de
 *    1440px ela renderizaria a tabela desktop inteira espremida.
 * 2. **Isto não é uma listagem de projetos: é a agenda de um dia.** Não há
 *    crachá de situação (a situação virou a cor), nem responsável, nem tempo de
 *    espera — nenhuma das colunas que a decisão 71 existe para manter
 *    consistentes entre telas. É mais perto do chip do que da tabela.
 *
 * **Sempre o dia selecionado, em qualquer vista** (decisão 72): a pergunta que
 * ela responde é sempre *o que acontece neste dia*, e uma lista que muda de
 * recorte conforme a vista obriga a reaprender a ler a cada troca de botão.
 *
 * **Item de mais de um dia aparece em todos os dias que atravessa**, com a
 * janela por extenso — quem clica no sábado do meio de um retiro está
 * perguntando se o Templo está livre, e "nada" seria falso.
 */
export function DayAgendaList({ day, items, onOpenItem }: DayAgendaListProps) {
  const parts = formatDayParts(`${day}T12:00:00-03:00`);

  return (
    <section aria-label={`O que acontece em ${parts.day} de ${parts.month}`} className="space-y-2">
      <h2 className="text-[0.6875rem] font-medium uppercase tracking-wider text-muted-foreground">
        {/* `first-letter:uppercase`, e não `capitalize`: aquele deixava
            "Terça-Feira" — em português só a primeira palavra sobe. Aqui tudo
            sobe por CSS, que é o registro de cabeçalho de seção da casa. */}
        <span>{longWeekday(day)}</span>
        {', '}
        {parts.day} de {monthName(day)}
      </h2>

      {items.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground">{messages.empty.calendarDay}</p>
      ) : (
        <ul className="stagger space-y-0.5">
          {items.map((item) => (
            <li key={keyOf(item)}>
              <ItemRow item={item} day={day} onOpen={onOpenItem} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Uma linha em três: **título** · *hora · espaço* · *situação*, com a mesma
 * matiz e o mesmo peso do chip da grade.
 *
 * A ordem é a da pergunta que se faz olhando o dia: primeiro *o que é*, depois
 * *quando e onde*, e por último *em que pé está*. O título ganha a linha
 * inteira porque era ele que a hora espremia — nome de projeto de igreja passa
 * fácil dos 30 caracteres, e numa coluna de 208px dividir a primeira linha com
 * o horário truncava justamente o que identifica o item.
 *
 * ⚠️ **Ela navega direto, sem passar pela folha** (§7). O clique no chip abre a
 * folha porque lá o alvo tem 2mm e a pessoa pode ter errado o dia; aqui cada
 * linha já é explícita — título inteiro, espaço, horário —, e uma folha
 * repetindo o que está escrito seria um passo a mais para chegar ao mesmo
 * lugar.
 */
function ItemRow({
  item,
  day,
  onOpen,
}: {
  item: CalendarItem;
  day: string;
  onOpen: (item: CalendarItem) => void;
}) {
  const spaces = item.kind === 'project' ? item.project.space_names : item.occurrence.space_names;
  const isFull = item.access === 'full';

  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className={cn(
        'flex w-full items-start gap-2.5 rounded-md px-2 py-1.5 text-left',
        'transition-[background-color,transform] duration-fast ease-out active:scale-[0.99]',
        'hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
      )}
    >
      {/* O filete, não um ponto: ele acompanha a altura da linha e repete
          exatamente o que a grade acabou de dizer sobre aquele item. */}
      <span
        aria-hidden="true"
        className={cn('mt-0.5 w-1 shrink-0 self-stretch rounded-full', dotClassOf(item))}
      />
      <span className="min-w-0 flex-1 space-y-0.5">
        <span
          className={cn(
            'block truncate text-sm',
            isFull ? 'font-medium text-foreground' : 'text-muted-foreground',
          )}
        >
          {titleOf(item)}
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {/* `tabular` só no horário: alinhar os dígitos entre linhas é o que
              deixa a coluna de horas legível de relance, e o nome do espaço não
              se beneficia disso. */}
          <span className="tabular">{timeLabel(item, day)}</span>
          {spaces.length > 0 ? ` · ${formatList(spaces)}` : null}
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {/* A situação POR EXTENSO, sempre — a cor é o canal rápido, nunca o
              único (§4.3). */}
          {statusLabelOf(item)}
        </span>
      </span>
    </button>
  );
}

/**
 * A hora, ou a janela por extenso quando o item atravessa o dia selecionado.
 *
 * "19:00" num sábado que é o meio de um retiro seria mentira por omissão: aquele
 * item não começa naquele dia. `Dia todo` é o que o olho precisa ali, e a janela
 * completa vem no `title` da folha.
 */
function timeLabel(item: CalendarItem, day: string): string {
  const start = startOf(item);
  const end = endOf(item);
  const startsToday = isSameZonedDay(start, `${day}T12:00:00-03:00`);

  if (!startsToday) return 'Dia todo';
  if (end && !isSameZonedDay(start, end)) return `${formatTime(start)} →`;
  return formatTime(start);
}

function keyOf(item: CalendarItem): string {
  return item.kind === 'project' ? item.project.id : item.occurrence.id;
}

/** `quinta-feira` — por extenso, porque o cabeçalho é o título da lista. */
function longWeekday(day: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    weekday: 'long',
  }).format(new Date(`${day}T12:00:00-03:00`));
}

function monthName(day: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    month: 'long',
  }).format(new Date(`${day}T12:00:00-03:00`));
}
